import { v4 as uuid } from 'uuid';

import {
    IVaccination,
    ConsentStatus,
    friendlyVaccinationCategories,
    friendlyManufacturers,
    Flags,
    v2,
    VaccinationType,
    VaccineManufacturer,
    constants,
} from '../../..';
import { getDefaults } from './helpers/getDefaults';

const getDate = (date?: string): Date | null => {
    if (!date) {
        return null;
    }
    try {
        return new Date(date);
    } catch {
        console.error('Invalid date', date);
        return null;
    }
};

const getManufacturer = (manufacturer?: string): string | null => {
    if (!manufacturer) {
        return null;
    }
    return friendlyManufacturers[manufacturer as VaccineManufacturer] || manufacturer;
};

export const vaccinationAdapter = (vaccination: IVaccination): v2.Vaccination => {
    const {
        hubId,
        vaccinationCategory,
        vaccinationType,
        consented,
        vaccineManufacturer,
        coAdministeredWith,
        itineraryId,
        vaccineBatchNumber,
        vaccineExpiryDate,
        vaccineSiteOfAdministration,
        flags,
        doseNumber,
        dateOfPreviousDose,
        dateOfDose1,
        dateOfDose2,
        dateOfDose3,
        dateOfDose3Primary,
        dateOfDose4,
        manufacturerOfPreviousDose,
        manufacturerOfDose1,
        manufacturerOfDose2,
        manufacturerOfDose3,
        manufacturerOfDose4,
    } = vaccination;

    const previousDoses = [
        {
            doseNumber: null,
            vaccinationType: null,
            date: getDate(dateOfPreviousDose),
            manufacturer: getManufacturer(manufacturerOfPreviousDose),
        },
        {
            doseNumber: 4,
            vaccinationType: VaccinationType.BOOSTER,
            date: getDate(dateOfDose4),
            manufacturer: getManufacturer(manufacturerOfDose4),
        },
        {
            doseNumber: 3,
            vaccinationType: VaccinationType.BOOSTER,
            date: getDate(dateOfDose3Primary),
            manufacturer: getManufacturer(manufacturerOfDose3),
        },
        {
            doseNumber: 3,
            vaccinationType: VaccinationType.PRIMARY,
            date: getDate(dateOfDose3),
            manufacturer: getManufacturer(manufacturerOfDose3),
        },
        {
            doseNumber: 2,
            vaccinationType: VaccinationType.PRIMARY,
            date: getDate(dateOfDose2),
            manufacturer: getManufacturer(manufacturerOfDose2),
        },
        {
            doseNumber: 1,
            vaccinationType: VaccinationType.PRIMARY,
            date: getDate(dateOfDose1),
            manufacturer: getManufacturer(manufacturerOfDose1),
        },
    ].filter(({ date, manufacturer }) => date !== null || manufacturer !== null);

    const consentStatusMapping: Record<ConsentStatus, v2.TConsentStatus> = {
        [ConsentStatus.CONSENTED]: v2.ConsentStatus.CONSENTED,
        [ConsentStatus.DID_NOT_CONSENT]: v2.ConsentStatus.DID_NOT_CONSENT,
        [ConsentStatus.UNKNOWN]: v2.ConsentStatus.UNKNOWN,
    };

    const defaults = getDefaults(vaccination);

    return {
        ...defaults,
        id: constants.NOT_CAPTURED,
        jobId: constants.NOT_CAPTURED,
        doseNumber,
        hubId,
        vaccinationCategoryId: vaccinationCategory,
        vaccinationCategory: {
            ...defaults,
            id: constants.NOT_CAPTURED,
            key: vaccinationCategory,
            name: friendlyVaccinationCategories[vaccinationCategory],
        },
        vaccinationType,
        consented: consentStatusMapping[consented] || null,
        vaccinationManufacturerId: vaccineManufacturer || null,
        vaccinationManufacturer: vaccineManufacturer
            ? {
                  ...defaults,
                  id: constants.NOT_CAPTURED,
                  key: vaccineManufacturer,
                  name: friendlyManufacturers[vaccineManufacturer] || vaccineManufacturer,
                  vaccinationCategoryId: constants.NOT_CAPTURED,
              }
            : null,
        coAdminGroupId: constants.NOT_CAPTURED,
        itineraryId,
        batchNumber: vaccineBatchNumber,
        expiryDate: vaccineExpiryDate ? getDate(vaccineExpiryDate) : null,
        siteOfAdministration: vaccineSiteOfAdministration,
        vaccinationFlags: flags?.map((flag) => {
            const flagKey = Object.entries(Flags).find(([, value]) => value === flag)?.[0] || flag;
            return {
                ...defaults,
                id: uuid(),
                vaccinationCategoryId: constants.NOT_CAPTURED,
                flagId: flagKey,
                vaccinationId: constants.NOT_CAPTURED,
                flag: {
                    ...defaults,
                    id: constants.NOT_CAPTURED,
                    key: flagKey,
                    name: flag,
                    vaccinationCategoryId: constants.NOT_CAPTURED,
                },
            };
        }),
        coAdminGroup: {
            ...defaults,
            id: constants.NOT_CAPTURED,
            vaccinations:
                coAdministeredWith?.map((coAdminVaccination) => ({
                    ...vaccinationAdapter({
                        ...vaccination,
                        vaccinationCategory: coAdminVaccination,
                        vaccineManufacturer: undefined,
                        coAdministeredWith: undefined,
                    }),
                    id: uuid(),
                    vaccinationType: null,
                })) || [],
        },
        previousDoses,
    };
};
